import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import React from "react";
import { Controller } from "react-hook-form";
import { formBuilderParams } from "../FormBuilder";
import { formRulesBuilder } from "../lib/FormBuilderRules";
import { useDefaultValueTriggerUpdate, useOnBlurRulesTrigger, useRemoveMandatoryError } from "../Utils/FormBuilderUtils";

/**
 * @typedef {import("../lib/FormBuilderFieldBuilder").baseFormComponentProps} baseFormComponentProps
 * @typedef {import("../lib/FormBuilderFieldBuilder").baseConfig} baseConfig
 * @typedef {import("../lib/FormBuilderFieldBuilder").textInputConfig} textInputConfig
 * @param {baseFormComponentProps & baseConfig & textInputConfig} props
 */
const FormBuilderTextInput = ({
  formProps,
  fieldName,
  defaultValue = "",
  defaultValueTriggerUpdate = false,
  mandatory = false,
  minLength,
  maxLength,
  rules,
  onBlurRules,
  customRule,
  inputType = "text",
  numberRange,
  asNumber,
  disableLineBreak = false,
  multiline = false,
  rows,
  label,
  subLabel,
  hideLabel,
  noErrorMessage = false,
  placeholder = "",
  customOnChangeFn,
  customOnEnterDownFn,
  customOnFocusFn,
  customOnBlurFn,
  disabled = false,
  readOnly = false,
}) => {
  const emptyValue = asNumber ? 0 : "";
  const isPercentage = inputType === "percentage";
  const _inputType = isPercentage ? "number" : inputType;

  useRemoveMandatoryError({ fieldName, mandatory, formProps });
  defaultValueTriggerUpdate && useDefaultValueTriggerUpdate({ fieldName, defaultValue, formProps });
  const { onBlurTrigger } = useOnBlurRulesTrigger({ fieldName, formProps, rules: onBlurRules || [] });

  return (
    <Controller
      key={fieldName}
      name={fieldName}
      control={formProps.control}
      defaultValue={defaultValue}
      rules={formRulesBuilder({ mandatory, minLength, maxLength, rules, customRule })}
      render={({ field: { value, onChange, ref }, fieldState: { error } }) => {
        return (
          <OutlinedText
            inputRef={ref}
            key={fieldName}
            type={_inputType}
            numberRange={numberRange}
            multiline={multiline}
            rows={rows}
            onEnterDownFn={(e) => {
              disableLineBreak && e.preventDefault();
              customOnEnterDownFn && customOnEnterDownFn(e);
            }}
            label={label}
            subLabel={subLabel}
            hideLabel={hideLabel === "removePadding"}
            emptyLabel={hideLabel === "keepPadding"}
            noErrorMessage={noErrorMessage}
            placeholder={placeholder}
            value={value ?? emptyValue}
            onchangeFn={(e) => {
              let newValue = e.target.value;
              if (asNumber && newValue != null && !Number.isNaN(parseFloat(newValue)) && /^-?\d+(\.\d+)?$/.test(newValue)) {
                /*
                  Will parse only if the following is true:
                  - asNumber is true
                  - newValue is not nullish
                  - newValue is not NaN under parseFloat
                  - newValue is a completed number, e.g. 1, 1.2, -1.2 etc., without a dangling decimal points
                 */
                newValue = parseFloat(newValue)
              };
              onChange(newValue);
              if (customOnChangeFn) customOnChangeFn(newValue, formProps);
            }}
            onfocusFn={(e) => {
              customOnFocusFn && customOnFocusFn(e)
            }}
            onblurFn={(e) => {
              onBlurTrigger();
              customOnBlurFn && customOnBlurFn(e)
            }}
            errorMessage={error?.message || ""}
            sxParams={{
              textError: !!error,
            }}
            divStyle={formBuilderParams.formItemStyle}
            disabled={disabled}
            readOnly={readOnly}
            mandatory={mandatory}
            percentage={isPercentage}
          />
        )
      }}
    />
  );
};

export default FormBuilderTextInput;
